import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Col, Tab, Tabs, Row, Modal, Button, Alert } from 'react-bootstrap';
import { getSessionStore } from '../../store/sessionStore';
import PortfolioCards from '../Preview/PortfolioCards';
import ProfileProfessionalLinksCard from './ProfileProfessionalLinksCard';
import UserInformationList from '../Preview/UserInformationList';
import UserPanel from '../Preview/UserPanel';
import UserProfileTabContent from './UserProfileTabContent';
import { ReactComponent as CloseIcon } from '../../images/close-cross.svg';
import { ReactComponent as PlusIcon } from '../../images/plus.svg';
import { ReactComponent as EditIcon } from '../../images/edit.svg';
import Spinner from 'react-bootstrap/Spinner';

import { ReturnedPortfolio } from '../Create-Portfolio/types';
import { API_ROUTES } from '../../config/api-routes';
import PromptsTabContent from './PromptDataTabContent';
import axiosInstance from '../../api/api';
import AddNewPromptStepper from '../AddNewPromptStepper';
import { primaryPrompts } from '../../config/generalPrompts';
import './UserProfile.css';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import AppBackButton from '../common/AppBackButton';
import OnboardingProgress from '../OnboardingProgress';
import {
  resolveDisplayJobTitle,
  startRoleSelectionFlow,
  userNeedsPromptSetup,
} from '../../utils/userProfile';
import { PageBusyLoader } from '../ui/PageBusyLoader';
import { applyPortfolioSnapshot } from '../../utils/portfolioRefresh';
import {
  PORTFOLIO_SAVE_FAILED,
  PORTFOLIO_SAVE_SUCCESS,
} from '../../utils/portfolioSectionMessages';
import { applyUserProfileFocus } from '../../utils/applyUserProfileFocus';

const UserProfile: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [key, setKey] = useState<string>('prompts');
  const [portfolioId, setPortfolioId] = useState<number>(0);
  const [isPortfolioPresent, setIsPortfolioPresent] = useState<boolean>(false);
  const [savePortfolioButton, setSavePortfolioButton] = useState<boolean>(false);
  const [showAlert, setShowAlert] = useState(false);
  const [likedPromptNotice, setLikedPromptNotice] = useState<string | null>(null);
  const [likedPromptAt, setLikedPromptAt] = useState<string | null>(null);

  const [portfolioData, setPortfolioData] = useState<ReturnedPortfolio>({
    overview: '',
    projects: [],
    references: [],
    languages: [],
    certifications: [],
    workCulture: [],
    accomplishments: [],
    organizations: [],
  });

  const [userData, setUserData] = useState<any>(null);
  const [promptsData, setPromptsData] = useState<any>([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [deletePortfolioContext, setPortfolioContextRemoval] = useState<boolean>(false);
  const [pageLoading, setPageLoading] = useState<boolean>(false);
  const [portfolioSaving, setPortfolioSaving] = useState<boolean>(false);
  const [deletingPortfolio, setDeletingPortfolio] = useState<boolean>(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const accessToken = localStorage.getItem('accessToken');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [promptArray, setPromptArray] = useState<string[]>([...primaryPrompts]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  //const [videoFiles, setVideoFiles] = useState<VideoFiles>({});
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [pendingFilesMap, __] = useState<Record<number, File[]>>({});
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [pendingAccFilesMap, ___] = useState<Record<number, File[]>>({});
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [pendingCertFilesMap, _] = useState<Record<number, File[]>>({});

  const MIN_PROMPTS = 4;
  const MAX_PROMPTS = 6;

  // A safe setter that prevents going over MAX when adding
  const setPromptsDataLimited = (update: any, { enforceLimits = true } = {}) => {
    setPromptsData((prev: any[]) => {
      const next = typeof update === 'function' ? update(prev) : update;

      if (Array.isArray(next) && enforceLimits) {
        if (next.length < MIN_PROMPTS) {
          toast.error(`You must keep at least ${MIN_PROMPTS} prompts`);
          return prev;
        }
        if (next.length > MAX_PROMPTS) {
          return prev;
        }
      }

      return next;
    });
  };

  const applySessionToState = useCallback(
    (userData: any, portfolioData: ReturnedPortfolio | null | undefined, promptsData: any[]) => {
      if (!userData?.username) {
        setPageLoading(false);
        navigate('/onboarding');
        return false;
      }

      if (portfolioData) {
        setPortfolioData({
          isOverviewEnabled: true,
          isProjectsEnabled: true,
          isReferencesEnabled: true,
          isLanguagesEnabled: true,
          isCertificationsEnabled: true,
          isWorkCultureEnabled: true,
          isOrganizationsEnabled: true,
          isAccomplishmentsEnabled: true,
          overview: portfolioData.overview || '',
          projects: portfolioData.projects || [],
          references: portfolioData.references || [],
          languages: portfolioData.languages || [],
          certifications: portfolioData.certifications || [],
          workCulture: portfolioData.workCulture || [],
          accomplishments: portfolioData.accomplishments || [],
          organizations: portfolioData.organizations || [],
        });

        setPortfolioContextRemoval(false);
        setIsPortfolioPresent(true);
        setPortfolioId(Number(portfolioData.id) || 0);
      }

      setUserData(userData);

      const safePrompts = Array.isArray(promptsData) ? promptsData : [];
      setPromptsData(safePrompts);

      return safePrompts.length > 0;
    },
    [navigate]
  );

  const getData = useCallback(
    async (options?: { force?: boolean; silent?: boolean }) => {
      if (!options?.silent) {
        setPageLoading(true);
      }

      const session = await getSessionStore().bootstrapSession({
        force: options?.force ?? true,
      });
      const userData = session.user;
      let promptsList = Array.isArray(session.prompts) ? session.prompts : [];
      const portfolioData = session.portfolio;

      if (promptsList.length === 0) {
        const retry = await getSessionStore().bootstrapSession({ force: true });
        promptsList = Array.isArray(retry.prompts) ? retry.prompts : [];
      }

      const hasPrompts = applySessionToState(
        userData,
        portfolioData as ReturnedPortfolio | null | undefined,
        promptsList
      );

      if (
        !hasPrompts &&
        userNeedsPromptSetup(promptsList.length, userData as Record<string, unknown> | null)
      ) {
        navigate('/get-started', { replace: true });
      }

      if (!options?.silent) {
        setPageLoading(false);
      }
    },
    [applySessionToState, navigate]
  );

  const didInit = useRef(false);
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    setSavePortfolioButton(false);

    if (!accessToken) return;

    void getData({ force: true });
  }, [accessToken, getData]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    const notice = params.get('likedPromptNotice');
    const likedAt = params.get('likedPromptAt');
    if (tab === 'prompts' || tab === 'portfolio') {
      setKey(tab);
    }
    if (notice) {
      setLikedPromptNotice(notice);
      setLikedPromptAt(likedAt);
    }
  }, [location.search]);

  useEffect(() => {
    if (pageLoading) {
      return;
    }

    const focus = new URLSearchParams(location.search).get('focus');
    if (!focus) {
      return;
    }

    const timer = window.setTimeout(() => applyUserProfileFocus(focus), 350);
    return () => window.clearTimeout(timer);
  }, [location.search, pageLoading, key]);

  const parseLikedPromptNotice = useCallback((message: string | null) => {
    const fallback = {
      recruiterName: 'A recruiter',
      promptText: '',
      rawMessage: message || '',
    };
    if (!message) return fallback;

    const promptMatch = message.match(/^(.*?)\s+liked your prompt:\s*"([^"]+)"\.?$/i);
    if (promptMatch) {
      return {
        recruiterName: promptMatch[1]?.trim() || 'A recruiter',
        promptText: promptMatch[2]?.trim() || '',
        rawMessage: message,
      };
    }

    const recruiterMatch = message.match(/^(.*?)\s+liked your prompt/i);
    return {
      recruiterName: recruiterMatch?.[1]?.trim() || 'A recruiter',
      promptText: '',
      rawMessage: message,
    };
  }, []);

  const likedPromptDetails = parseLikedPromptNotice(likedPromptNotice);

  const handleDeletePortfolio = async () => {
    setDeletingPortfolio(true);
    if (!portfolioId) {
      console.log('Portfolio ID is not set');
      setDeletingPortfolio(false);
      return;
    }

    const response = await axiosInstance.delete(
      `${API_ROUTES.PORTFOLIO.DELETE_PORTFOLIO}/${portfolioId}`
    );

    if (!response || response.status !== 200) {
      console.log('Error', response);
      setDeletingPortfolio(false);
      return;
    }

    setPortfolioData({
      overview: '',
      projects: [],
      references: [],
      languages: [],
      certifications: [],
      workCulture: [],
      accomplishments: [],
      organizations: [],
    });
    setIsPortfolioPresent(false);
    setShowModal(false);
    setPortfolioContextRemoval(true);
    setKey('prompts');
    toast.success('Portfolio deleted successfully');
    window.location.reload();
  };

  const submitUpdatedPortfolio = useCallback(async () => {
    setPortfolioSaving(true);
    const resolvedPortfolioId = Number(portfolioId) || 0;
    if (isPortfolioPresent && resolvedPortfolioId > 0) {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        console.error('Access token is missing');
        setPortfolioSaving(false);
        return;
      }

      if (portfolioData) {
        try {
          // 1️⃣ Save portfolio JSON info first
          const response = await axiosInstance.post(
            `${API_ROUTES.PORTFOLIO.EDIT_PORTFOLIO}/${resolvedPortfolioId}`,
            portfolioData
          );

          if (!response || response.status !== 200) {
            if (setSavePortfolioButton) {
              setSavePortfolioButton(true);
              setShowModal(true);
              toast.error(PORTFOLIO_SAVE_FAILED);
            }
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const savedPortfolio = response.data?.portfolio ?? response.data?.data?.portfolio;
          if (savedPortfolio) {
            applyPortfolioSnapshot(setPortfolioData, savedPortfolio);
          }

          // 2️⃣ Upload any pending files (from pendingFilesMap)
          if (pendingFilesMap && Object.keys(pendingFilesMap).length > 0) {
            for (const [projectIndex, files] of Object.entries(pendingFilesMap)) {
              const proj = portfolioData.projects?.[Number(projectIndex)];
              if (!proj?.id) continue;

              for (const file of files) {
                const fileId = uuidv4();
                const fd = new FormData();
                fd.append('info', JSON.stringify({ project: { id: proj.id, fileId } }));
                fd.append(fileId, file, file.name);

                await axiosInstance.post(API_ROUTES.PORTFOLIO.UPLOAD_FILES, fd);
              }
            }
          }

          if (pendingAccFilesMap && Object.keys(pendingAccFilesMap).length > 0) {
            for (const [accIndex, files] of Object.entries(pendingAccFilesMap)) {
              const acc = portfolioData.accomplishments?.[Number(accIndex)];
              if (!acc?.id) continue;

              for (const file of files) {
                const fileId = uuidv4();
                const fd = new FormData();
                fd.append('info', JSON.stringify({ accomplishment: { id: acc.id, fileId } }));
                fd.append(fileId, file, file.name);

                await axiosInstance.post(API_ROUTES.PORTFOLIO.UPLOAD_FILES, fd);
              }
            }
          }

          if (pendingCertFilesMap && Object.keys(pendingCertFilesMap).length > 0) {
            for (const [accIndex, files] of Object.entries(pendingCertFilesMap)) {
              const acc = portfolioData.certifications?.[Number(accIndex)];
              if (!acc?.id) continue;

              for (const file of files) {
                const fileId = uuidv4();
                const fd = new FormData();
                fd.append('info', JSON.stringify({ certification: { id: acc.id, fileId } }));
                fd.append(fileId, file, file.name);

                await axiosInstance.post(API_ROUTES.PORTFOLIO.UPLOAD_FILES, fd);
              }
            }
          }

          // 3️⃣ Success UI handling
          if (setSavePortfolioButton) {
            setSavePortfolioButton(false);
          }

          if (getData) {
            await getData({ force: true, silent: true });
          }
          await getSessionStore().refreshUser({ force: true });
          window.dispatchEvent(new Event('portfolio-updated'));
          toast.success(PORTFOLIO_SAVE_SUCCESS);
        } catch (error) {
          toast.error(PORTFOLIO_SAVE_FAILED);
          console.error('Failed to update portfolio:', error);
        } finally {
          setPortfolioSaving(false);
          setSavePortfolioButton(false);
        }
      } else {
        console.error('Portfolio data is missing');
        setPortfolioSaving(false);
      }
    } else {
      // 🆕 Creating portfolio case (untouched except for consistency)
      const hasValue = (value?: string | null) =>
        typeof value === 'string' ? value.trim().length > 0 : !!value;
      const isValidDate = (value?: string | Date | null) => {
        if (!value) return false;
        const date = value instanceof Date ? value : new Date(value);
        return !Number.isNaN(date.getTime());
      };

      const cleanedProjects = portfolioData.projects?.filter(
        (project) => hasValue(project.name) && hasValue(project.description)
      );
      const cleanedReferences = portfolioData.references?.filter(
        (reference) =>
          hasValue(reference.name) &&
          hasValue(reference.email) &&
          hasValue(reference.role) &&
          hasValue(reference.organization)
      );
      const cleanedLanguages = portfolioData.languages?.filter(
        (language) => hasValue(language.language) && hasValue(language.level)
      );
      const cleanedCertifications = portfolioData.certifications?.filter(
        (certification) => hasValue(certification.name) && isValidDate(certification.date)
      );
      const cleanedOrganizations = portfolioData.organizations?.filter(
        (organization) =>
          hasValue(organization.name) &&
          isValidDate(organization.startDate) &&
          isValidDate(organization.endDate) &&
          hasValue(organization.description) &&
          hasValue(organization.link)
      );
      const cleanedAccomplishments = portfolioData.accomplishments
        ?.map((accomplishment) => ({
          ...accomplishment,
          links: (accomplishment.links || []).filter((link) => hasValue(link)),
        }))
        .filter(
          (accomplishment) =>
            hasValue(accomplishment.name) &&
            hasValue(accomplishment.description) &&
            isValidDate(accomplishment.date)
        );
      const cleanedWorkCulture = portfolioData.workCulture?.filter((item) => hasValue(item));

      const info: any = {};
      if (portfolioData.overview) info.overview = portfolioData.overview;
      if (cleanedLanguages?.length) info.languages = cleanedLanguages;
      if (cleanedWorkCulture?.length) info.workCulture = cleanedWorkCulture;
      if (cleanedAccomplishments?.length) info.accomplishments = cleanedAccomplishments;
      if (cleanedCertifications?.length) info.certifications = cleanedCertifications;
      if (cleanedProjects?.length) info.projects = cleanedProjects;
      if (cleanedOrganizations?.length) info.organizations = cleanedOrganizations;
      if (cleanedReferences?.length) info.references = cleanedReferences;

      const fd = new FormData();
      fd.append('info', JSON.stringify(info));

      try {
        const res = await axiosInstance.post(API_ROUTES.PORTFOLIO.CREATE_PORTFOLIO_SIMPLE, info);
        const newId = res?.data?.portfolio?.id;
        if (newId) {
          setPortfolioId(newId);
          setIsPortfolioPresent(true);
          setSavePortfolioButton(false);
          toast.success('Portfolio created successfully');
          await axiosInstance.post(API_ROUTES.PORTFOLIO.ACTIVATE_PORTFOLIO);
          await getData({ force: true, silent: true });
          await getSessionStore().refreshUser({ force: true });
          window.dispatchEvent(new Event('portfolio-updated'));
        } else {
          toast.error('Could not create portfolio');
        }
      } catch (e) {
        toast.error('Failed to create portfolio');
        console.error(e);
      } finally {
        setPortfolioSaving(false);
        setSavePortfolioButton(false);
      }
    }
  }, [
    getData,
    isPortfolioPresent,
    pendingAccFilesMap,
    pendingCertFilesMap,
    pendingFilesMap,
    portfolioData,
    portfolioId,
  ]);

  useEffect(() => {
    if (!savePortfolioButton || portfolioSaving) return;
    submitUpdatedPortfolio();
  }, [savePortfolioButton, portfolioSaving, submitUpdatedPortfolio]);

  /* const navigateToPortfolioCreationPage = () => {
    navigate('/create-portfolio?mode=create');
  }; */

  const handleShowModal = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const displayJobTitle = resolveDisplayJobTitle(promptsData, userData);

  return (
    <>
      {pageLoading ? (
        <PageBusyLoader pageName="Profile" />
      ) : (
        <div className="container user-profile-page">
          {portfolioSaving ? <PageBusyLoader pageName="Profile" overlay /> : null}
          <div className="user-profile-page-back">
            <AppBackButton to="/dashboard" />
          </div>
          <Row>
            <OnboardingProgress />
            <Col className="mb-5">
              {(promptsData?.length ?? 0) === 0 ? (
                <Alert variant="light" className="user-profile-role-setup-alert mb-3">
                  Select your job role and create screening prompts.{' '}
                  <Button
                    variant="link"
                    className="p-0 align-baseline user-profile-role-setup-link"
                    onClick={() => startRoleSelectionFlow(navigate)}
                  >
                    Go to role selection
                  </Button>
                </Alert>
              ) : null}
              <Row className="row-sticky">
                <Col md={4} sm={12}>
                  <UserPanel
                    prompts={promptsData}
                    portfolio={portfolioData}
                    userData={userData}
                    savePortfolioButton={savePortfolioButton}
                    setSavePortfolioButton={setSavePortfolioButton}
                    portfolioId={portfolioId}
                    getData={getData}
                    setUserData={setUserData}
                    jobTitle={displayJobTitle}
                    hideProfessionalLinks
                  />

                  <UserInformationList userData={userData} />
                  <PortfolioCards
                    portfolio={portfolioData}
                    userProfile="profile"
                    sections="sidebar"
                    setPortfolioData={setPortfolioData}
                    setSavePortfolioButton={setSavePortfolioButton}
                  />
                </Col>
                <Col md={8} sm={12}>
                  <div className="tab-container-with-button">
                    <Tabs
                      id="controlled-tab"
                      activeKey={key}
                      onSelect={(k) => {
                        if (k !== 'portfolio' || isPortfolioPresent) {
                          setKey(k as string); // Allow tab switching only if portfolio is present or another tab is clicked
                        }
                      }}
                      className="mb-3 tab-menu"
                    >
                      <Tab
                        eventKey="prompts"
                        title={
                          <div
                            className={`nav-item-wrapper d-flex align-items-center justify-content-between ${
                              key === 'prompts' ? 'active-tab' : ''
                            }`}
                          >
                            <span className="tab-title">My Screening Prompts</span>
                            {(promptsData?.filter(Boolean).length || 0) < MAX_PROMPTS ? (
                              <div className="add-prompt-slot">
                                <AddNewPromptStepper
                                  prompts={promptsData}
                                  setPromptsData={setPromptsDataLimited}
                                />
                              </div>
                            ) : null}
                          </div>
                        }
                      >
                        <PromptsTabContent
                          prompts={promptsData}
                          setPromptsData={setPromptsDataLimited}
                        />
                        <div className="profile-right-extras">
                          <ProfileProfessionalLinksCard
                            userData={userData}
                            setUserData={setUserData}
                          />
                          <PortfolioCards
                            portfolio={portfolioData}
                            userProfile="profile"
                            sections="references"
                            setPortfolioData={setPortfolioData}
                            setSavePortfolioButton={setSavePortfolioButton}
                          />
                        </div>
                      </Tab>
                      <Tab
                        eventKey="portfolio"
                        title={
                          <div
                            className={`nav-item-wrapper d-flex align-items-center justify-content-between ${
                              key === 'portfolio' ? 'active-tab' : ''
                            }`}
                          >
                            <span className="portfolio-title tab-title">Portfolio</span>
                            {isPortfolioPresent ? (
                              <div className="portfolio-actions">
                                <span className="user-info-edit-button">
                                  <EditIcon
                                    style={{ width: '18px', height: '18px' }}
                                    /*  onClick={() =>
                                      navigate('/create-portfolio?mode=edit')
                                    } */
                                  />
                                </span>
                                <span className="delete-button" onClick={handleShowModal}>
                                  <CloseIcon style={{ width: '20px', height: '20px' }} />
                                </span>
                              </div>
                            ) : (
                              <span
                                className="add-button"
                                onClick={(e) => {
                                  e.stopPropagation();

                                  // Portfolio Welcome Message
                                  setShowAlert(true);

                                  // Simulate enabling an empty portfolio on the current tab
                                  setPortfolioData({
                                    isOverviewEnabled: true,
                                    isProjectsEnabled: true,
                                    isReferencesEnabled: true,
                                    isLanguagesEnabled: true,
                                    isCertificationsEnabled: true,
                                    isWorkCultureEnabled: true,
                                    isOrganizationsEnabled: true,
                                    isAccomplishmentsEnabled: true,
                                    overview: '',
                                    projects: [],
                                    references: [],
                                    languages: [],
                                    certifications: [],
                                    workCulture: [],
                                    accomplishments: [],
                                    organizations: [],
                                  });

                                  setKey('portfolio'); // ✅ Manually activate the portfolio tab here
                                }}
                              >
                                <PlusIcon style={{ width: '20px', height: '20px' }} />
                              </span>
                            )}
                          </div>
                        }
                      >
                        <UserProfileTabContent
                          portfolioId={portfolioId}
                          portfolio={portfolioData}
                          setPortfolioData={setPortfolioData}
                          setSavePortfolioButton={setSavePortfolioButton}
                          savePortfolioButton={savePortfolioButton}
                          deletePortfolioContext={deletePortfolioContext}
                          getData={getData}
                        />
                      </Tab>
                    </Tabs>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>

          <Modal show={showSaveModal} onHide={() => setShowSaveModal(false)}>
            <Modal.Header>
              <Modal.Title>Save Successful</Modal.Title>
            </Modal.Header>
            <Modal.Body>Your changes have been saved successfully!</Modal.Body>
            <Modal.Footer>
              <Button variant="success" onClick={() => setShowSaveModal(false)}>
                Close
              </Button>
            </Modal.Footer>
          </Modal>

          <Modal show={showModal} onHide={handleCloseModal} centered>
            <Modal.Header>
              <Modal.Title>Confirm Deletion</Modal.Title>
            </Modal.Header>
            <Modal.Body>Are you sure you want to permanently delete your portfolio?</Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleDeletePortfolio} disabled={deletingPortfolio}>
                {deletingPortfolio ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      )}

      <Modal show={showAlert} onHide={() => setShowAlert(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Welcome 🎉</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Welcome to your <strong>SkipScreen Portfolio!</strong>
          </p>
          <p>
            Add projects, accomplishments, and references to showcase your talent and make your
            profile even stronger alongside your prompts.
          </p>
        </Modal.Body>
      </Modal>

      <Modal
        show={Boolean(likedPromptNotice)}
        onHide={() => {
          setLikedPromptNotice(null);
          setLikedPromptAt(null);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Recruiter Liked Prompt</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-2">
            <strong>Recruiter:</strong> {likedPromptDetails.recruiterName}
          </div>
          {likedPromptDetails.promptText ? (
            <div className="mb-2">
              <strong>Prompt:</strong> "{likedPromptDetails.promptText}"
            </div>
          ) : null}
          {likedPromptAt ? (
            <div className="mb-2 text-muted">
              <small>
                {new Date(likedPromptAt).toLocaleDateString()} at{' '}
                {new Date(likedPromptAt).toLocaleTimeString()}
              </small>
            </div>
          ) : null}
          {!likedPromptDetails.promptText ? (
            <p className="mb-0">{likedPromptDetails.rawMessage}</p>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="success"
            onClick={() => {
              setLikedPromptNotice(null);
              setLikedPromptAt(null);
            }}
          >
            Back to Profile
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UserProfile;
